import type { Unit } from "@/lib/types/unit";
import type { TerrainSpeedModifiers } from "@/lib/types/terrain-speed-modifiers";
import type { UnitTraitEffectsMap } from "@/lib/types/unit-trait-effects";
import { isTraitActive } from "@/lib/damage-calculator/trait-activation";
import { Motion } from "@/lib/types/motion";

/*
 * Scope: speeds here use terrain-speed-modifiers (forest/building/ruins, etc.) only.
 * Amphibious water movement is not modeled; the _amphibie trait does not add a water speed row.
 */

export type TerrainSpeed = {
  terrainName: string;
  speed: number;
  baseSpeed?: number;
  isModified?: boolean;
};

/**
 * Calculates the unit's speed on different terrain types
 * @param unit The unit to calculate speeds for
 * @param terrainSpeedModifiers The terrain speed modifier data
 * @param traitOverrides Optional map of trait overrides (for manual toggling)
 * @param unitTraitEffects Optional unit trait effects data (for mountaineer modifier)
 * @param baseSpeed Optional base speed (if different from unit.speed, for showing modifications)
 * @param modifiedSpeed Optional modified speed (if traits affect speed)
 * @returns Array of terrain speeds, or empty array if unit doesn't have valid movement type
 */
export function calculateTerrainSpeeds(
  unit: Unit,
  terrainSpeedModifiers: TerrainSpeedModifiers,
  traitOverrides?: Map<string, boolean | undefined>,
  unitTraitEffects?: UnitTraitEffectsMap,
  baseSpeed?: number,
  modifiedSpeed?: number
): TerrainSpeed[] {
  // Skip if unit doesn't have unitMovingType
  if (!unit.unitMovingType) {
    return [];
  }

  // Skip flying units (they don't use terrain modifiers)
  if (unit.unitMovingType === 'Flying') {
    return [];
  }

  const terrainSpeeds: TerrainSpeed[] = [];
  const unitMoveType = unit.unitMovingType;

  // Check if mountaineer trait is active
  const mountaineerTraitName = '_mountaineer';
  let mountaineerModifierFactor: number | undefined = undefined;
  
  if (unitTraitEffects && unit.specialities?.includes(mountaineerTraitName)) {
    const mountaineerEffect = unitTraitEffects[mountaineerTraitName];
    if (mountaineerEffect) {
      // Create a dummy context for trait activation checking
      const dummyContext = {
        sourceUnit: unit,
        targetUnit: unit,
        distance: 0,
        sourceMotion: Motion.STATIC,
        targetMotion: Motion.STATIC,
        sourceTraitOverrides: traitOverrides,
        targetTraitOverrides: undefined,
      };
      
      const activationResult = isTraitActive(mountaineerTraitName, dummyContext, true);
      if (activationResult.isActive) {
        // Find the UnitEffectAddTerrainSpeedModifier effect
        const terrainSpeedEffect = Object.values(mountaineerEffect.effects).find(
          (effect) => effect.type === 'TUnitEffectAddTerrainSpeedModifierDescriptor'
        );
        
        if (terrainSpeedEffect && typeof terrainSpeedEffect.TerrainSpeedModifierFactor === 'number') {
          mountaineerModifierFactor = terrainSpeedEffect.TerrainSpeedModifierFactor;
        }
      }
    }
  }

  // For each terrain modifier, find matching movement type
  for (const terrainModifier of terrainSpeedModifiers) {
    // Get all movement type keys (Wheel, Track, Infantry, AllTerrainWheel)
    const moveTypes = Object.keys(terrainModifier.movementTypes);
    
    // Find a movement type that matches the unit's moving type
    // Use .includes() to handle cases like "WheelAmphibious" matching "Wheel"
    const foundMoveType = moveTypes.find((moveType) =>
      unitMoveType.includes(moveType)
    );

    if (foundMoveType) {
      const modifier = terrainModifier.movementTypes[
        foundMoveType as keyof typeof terrainModifier.movementTypes
      ];
      
      // Apply mountaineer modifier if active
      // TerrainSpeedModifierFactor is an additive factor that modifies the terrain speed modifier
      // The effect name "UnitEffectAddTerrainSpeedModifier" suggests it adds to the base modifier
      // TerrainSpeedModifierFactor: 0 means "no additional penalty" = full speed (1.0)
      let finalModifierValue = modifier.value;
      if (mountaineerModifierFactor !== undefined) {
        // If TerrainSpeedModifierFactor is 0, it means no penalty (full speed = 1.0)
        // Otherwise, add it to the base modifier (clamped to max 1.0)
        if (mountaineerModifierFactor === 0) {
          finalModifierValue = 1.0; // No terrain penalty
        } else {
          // Add the factor to the base modifier, but cap at 1.0
          finalModifierValue = Math.min(1.0, modifier.value + mountaineerModifierFactor);
        }
      }
      
      // Use modified speed if provided, otherwise use base unit speed
      const speedToUse = modifiedSpeed !== undefined ? modifiedSpeed : unit.speed;
      // For base speed, use provided baseSpeed or fall back to unit.speed
      const baseSpeedToUse = baseSpeed !== undefined ? baseSpeed : unit.speed;
      
      const calculatedSpeed = Math.round(speedToUse * finalModifierValue);
      const calculatedBaseSpeed = Math.round(baseSpeedToUse * modifier.value);
      
      // Check if the calculated terrain speeds are actually different
      const isModified = calculatedSpeed !== calculatedBaseSpeed;
      
      terrainSpeeds.push({
        terrainName: terrainModifier.name,
        speed: calculatedSpeed,
        baseSpeed: isModified ? calculatedBaseSpeed : undefined,
        isModified: isModified,
      });
    }
  }

  return terrainSpeeds;
}

/**
 * Formats a terrain name for display
 * @param terrainName The terrain name (e.g., "forest", "building")
 * @returns Formatted terrain name (e.g., "Forest", "Building")
 */
export function formatTerrainName(terrainName: string): string {
  return terrainName.charAt(0).toUpperCase() + terrainName.slice(1);
}

